import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip,
  MenuItem, TextField, Button, Divider,
} from '@mui/material';
import { Home, Groups, WarningAmber, Save } from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

export default function TransportGroupingBoard() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [buses, setBuses] = useState([]);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [loading, setLoading] = useState(true);
  const [pick, setPick] = useState({}); // groupKey -> faNo for "assign to all"
  const [edits, setEdits] = useState({}); // admNo -> {morning, evening}

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = async () => {
    try {
      const [g, b] = await Promise.all([
        axios.get(`${API}/api/fleet/grouping`, { headers }),
        axios.get(`${API}/api/fleet/buses`, { headers }),
      ]);
      setGroups(g.data); setBuses(b.data); setError('');
    } catch (err) { setError(err.response?.data?.error || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const busCap = (faNo) => buses.find(b => b.faNo === faNo)?.profile?.capacity || null;
  const busLabel = (b) => `${b.faName || b.faNo}${b.profile?.capacity ? ` (${b.profile.capacity} seats)` : ''}`;

  const assignOne = async (admNo, morning, evening) => {
    await axios.post(`${API}/api/fleet/assign`, { admNo, morningBusFaNo: morning, eveningBusFaNo: evening, status: 'Active' }, { headers });
  };

  const assignGroup = async (grp, key) => {
    const faNo = pick[key];
    if (!faNo) return setError('Pick a bus first');
    try {
      for (const l of grp.learners) await assignOne(l.admNo, faNo, faNo);
      setOk(`Assigned ${grp.learners.length} learner(s) to a bus`); setError(''); load();
    } catch (err) { setError(err.response?.data?.error || 'Assign failed'); }
  };

  const saveLearner = async (l) => {
    const e = edits[l.admNo] || {};
    try {
      await assignOne(l.admNo, e.morning ?? l.morningBusFaNo, e.evening ?? l.eveningBusFaNo);
      setOk(`Saved ${l.name}`); setError(''); load();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); }
  };

  if (loading) return (<Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}><CircularProgress /></Box>);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center"><Groups sx={{ mr: 1, color: '#01579b' }} /><Typography variant="h5" sx={{ color: '#01579b' }}>Grouping &amp; Allocation</Typography></Box>
        <IconButton onClick={() => navigate('/fleet')} title="Fleet Dashboard"><Home /></IconButton>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Learners who opted into the bus, grouped by stage, level and usage. Allocation is manual — pick a bus per group (or per learner). Capacity warnings are advisory.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert>}

      {groups.length === 0 ? <Alert severity="info">No learners have opted into the bus yet. Mark a learner's transport on their profile.</Alert> : groups.map((grp, gi) => {
        const key = `${grp.pickupLocation}|${grp.levelCategory}|${grp.usage}`;
        const cap = busCap(pick[key]);
        const over = cap != null && grp.count > cap;
        return (
          <Card elevation={2} key={key + gi} sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{grp.locationName}</Typography>
                <Chip label={grp.levelCategory} size="small" />
                <Chip label={grp.usage} size="small" color={grp.usage === 'One-way' ? 'warning' : 'primary'} variant="outlined" />
                {grp.routeCode && <Chip label={`Route ${grp.routeCode}`} size="small" variant="outlined" />}
                <Chip label={`${grp.count} learner(s)`} size="small" color="info" />
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                <TextField select size="small" label="Assign whole group to bus" sx={{ minWidth: 260 }}
                  value={pick[key] || ''} onChange={e => setPick({ ...pick, [key]: e.target.value })}>
                  <MenuItem value="">(choose bus)</MenuItem>
                  {buses.map(b => <MenuItem key={b.faNo} value={b.faNo}>{busLabel(b)}</MenuItem>)}
                </TextField>
                <Button variant="outlined" onClick={() => assignGroup(grp, key)}>Assign to all</Button>
                {over && <Chip icon={<WarningAmber />} color="warning" label={`Group (${grp.count}) exceeds bus capacity (${cap}) — use a bigger bus`} />}
              </Box>
              <Divider sx={{ my: 1 }} />
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell><strong>Adm No</strong></TableCell><TableCell><strong>Name</strong></TableCell><TableCell><strong>Class</strong></TableCell>
                  <TableCell><strong>Morning bus</strong></TableCell><TableCell><strong>Evening bus</strong></TableCell><TableCell></TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {grp.learners.map(l => {
                    const e = edits[l.admNo] || {};
                    return (
                      <TableRow key={l.admNo}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{l.admNo}</TableCell>
                        <TableCell>{l.name}</TableCell>
                        <TableCell>{l.classCode}</TableCell>
                        <TableCell>
                          <TextField select size="small" sx={{ minWidth: 150 }} value={e.morning ?? l.morningBusFaNo ?? ''}
                            onChange={ev => setEdits({ ...edits, [l.admNo]: { ...e, morning: ev.target.value } })}>
                            <MenuItem value="">(none)</MenuItem>
                            {buses.map(b => <MenuItem key={b.faNo} value={b.faNo}>{b.faName || b.faNo}</MenuItem>)}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField select size="small" sx={{ minWidth: 150 }} value={e.evening ?? l.eveningBusFaNo ?? ''}
                            onChange={ev => setEdits({ ...edits, [l.admNo]: { ...e, evening: ev.target.value } })}>
                            <MenuItem value="">(none)</MenuItem>
                            {buses.map(b => <MenuItem key={b.faNo} value={b.faNo}>{b.faName || b.faNo}</MenuItem>)}
                          </TextField>
                        </TableCell>
                        <TableCell><Button size="small" startIcon={<Save />} onClick={() => saveLearner(l)}>Save</Button></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
}
